// useDashboard() — the dashboard's platform state and services, from any component of an app
// created by createDashboard() (IA_ABSORPTION_PLAN Phase 1 step 6b-2b). createDashboard creates
// ONE dashboard per app (./dashboardContext.js) and `app.provide`s it under DASHBOARD_KEY; this
// injects it. README "useDashboard()" has the field table.
import { inject } from 'vue';

/**
 * The injection key createDashboard provides the dashboard under. Exported for tests and custom
 * bootstraps (`app.provide(DASHBOARD_KEY, fakeDashboard)`); components call useDashboard().
 * `Symbol.for`, so two copies of this module (never expected) still agree on the key.
 */
export const DASHBOARD_KEY = Symbol.for('@rdrive/plugin-dashboard-shell:dashboard');

/**
 * The app's dashboard: `{ params, missingParams, settings, setValues, setScopedToken, fetcher,
 * api, duckdb, conn, db, isDuckDBInitialized, initDuckDB, loader, dataLoaded, filters,
 * newFilterContext, analytics, start, stop }`.
 *
 * Call it where `inject()` works: in a component's `setup()` (or a composable called from one),
 * or inside `app.runWithContext(() => useDashboard())`. Throws when there is no dashboard — an
 * app not created by createDashboard(), or a call outside an injection context.
 *
 * @returns {import('./dashboardContext.js').Dashboard}
 */
export function useDashboard() {
  const dashboard = inject(DASHBOARD_KEY, null);
  if (!dashboard) {
    throw new Error(
      '@rdrive/plugin-dashboard-shell: useDashboard() found no dashboard. Call it in a component setup ' +
        '(or app.runWithContext) of an app created by createDashboard().',
    );
  }
  return dashboard;
}
